import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { chatThreadsService } from "@/services/chatThreadsService";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import { topicsService } from "@/services/topicsService";
import {
  History,
  MessageSquare,
  Layers,
  Tag,
  ChevronRight,
  Clock,
  Sparkles,
} from "lucide-react";

function safeParseDate(
  ...values: (string | number | Date | null | undefined)[]
): Date | null {
  for (const v of values) {
    if (v == null) continue;
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return "—";
  return formatDistanceToNow(date, { addSuffix: true });
}

type ContinueTab = "threads" | "spaces" | "topics";

interface ContinueItem {
  id: string;
  title: string;
  subtitle?: string;
  updatedAt: Date | null;
  path: string;
  icon: typeof MessageSquare;
  color: string;
}

function ContinueTabPanel({
  items,
  emptyTitle,
  emptyDescription,
  primaryCta,
  onPrimaryCta,
}: {
  items: ContinueItem[];
  emptyTitle: string;
  emptyDescription: string;
  primaryCta: string;
  onPrimaryCta: () => void;
}) {
  const [, navigate] = useLocation();
  const mostRecent = items[0];

  if (items.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="font-medium text-sm">{emptyTitle}</p>
        <p className="text-xs mt-1">{emptyDescription}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onPrimaryCta}>
          {primaryCta}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {mostRecent && (
        <button
          type="button"
          onClick={() => navigate(mostRecent.path)}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 hover:border-blue-500/40 transition-all text-left"
          data-testid="button-continue-hero"
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${mostRecent.color}20` }}
          >
            <mostRecent.icon className="w-5 h-5" style={{ color: mostRecent.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{mostRecent.title}</div>
            {mostRecent.subtitle && (
              <div className="text-xs text-muted-foreground truncate">{mostRecent.subtitle}</div>
            )}
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              {mostRecent.updatedAt && <Clock className="w-3 h-3" />}
              {formatRelativeTime(mostRecent.updatedAt)}
            </div>
          </div>
          <div className="flex items-center gap-1 text-primary shrink-0">
            <span className="text-xs font-medium">Continue</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </button>
      )}
      {items.length > 1 && (
        <div className="space-y-1.5">
          <h4 className="text-xs font-medium text-muted-foreground px-1">Recent</h4>
          {items.slice(1, 5).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.path)}
              className="w-full flex items-center gap-2 p-2 rounded-lg border hover:bg-muted/50 transition-colors text-left group"
              data-testid={`button-continue-recent-${item.id}`}
            >
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${item.color}15` }}
              >
                <item.icon className="w-3.5 h-3.5" style={{ color: item.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{item.title}</div>
                <div className="text-xs text-muted-foreground">
                  {formatRelativeTime(item.updatedAt)}
                </div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ContinueWhereYouLeftOffCard() {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const [, navigate] = useLocation();
  const [tab, setTab] = useState<ContinueTab>("threads");

  const { data: threads = [] } = useQuery({
    queryKey: ["home-continue-threads", user?.id, currentWorkspaceId ?? null],
    queryFn: async () => {
      const all = await chatThreadsService.getThreadSummaries(currentWorkspaceId ?? null);
      return all.slice(0, 5);
    },
    enabled: !!user?.id,
  });

  const { data: spaces = [] } = useQuery({
    queryKey: ["home-continue-spaces", user?.id, currentWorkspaceId ?? null],
    queryFn: async () => {
      const all = await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
      return [...all]
        .sort(
          (a, b) =>
            new Date(b.updatedAt ?? b.createdAt ?? 0).getTime() -
            new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
        )
        .slice(0, 5);
    },
    enabled: !!user?.id,
  });

  const { data: topics = [] } = useQuery({
    queryKey: ["home-continue-topics", user?.id, currentWorkspaceId ?? null],
    queryFn: async () => {
      const all = await topicsService.getAllTopics(currentWorkspaceId ?? null);
      return all.slice(0, 5);
    },
    enabled: !!user?.id,
  });

  const threadItems: ContinueItem[] = useMemo(
    () =>
      threads.map((thread) => ({
        id: thread.id,
        title: thread.title || "Untitled Chat",
        subtitle: thread.topic || undefined,
        updatedAt: safeParseDate(thread.updatedAt, thread.createdAt),
        icon: MessageSquare,
        color: "#3b82f6",
        path: `/chat?threadId=${thread.id}`,
      })),
    [threads]
  );

  const spaceItems: ContinueItem[] = useMemo(
    () =>
      spaces.map((space) => ({
        id: space.id,
        title: space.name,
        subtitle: space.description ?? undefined,
        updatedAt: safeParseDate(space.updatedAt, space.createdAt),
        icon: Layers,
        color: space.color || "#8b5cf6",
        path: `/spaces/${space.id}`,
      })),
    [spaces]
  );

  const topicItems: ContinueItem[] = useMemo(
    () =>
      topics.map((topic) => ({
        id: topic.id,
        title: topic.name,
        subtitle: topic.description ?? undefined,
        updatedAt: safeParseDate(topic.updatedAt, topic.createdAt),
        icon: Tag,
        color: topic.color || "#f59e0b",
        path: `/spaces/${topic.spaceId}?tab=topics&expandTopic=${encodeURIComponent(topic.id)}`,
      })),
    [topics]
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="w-4 h-4 text-blue-500" />
          Continue Where You Left Off
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <Tabs value={tab} onValueChange={(v) => setTab(v as ContinueTab)}>
          <TabsList className="grid w-full grid-cols-3 mb-3">
            <TabsTrigger value="threads" className="text-xs">
              Threads
            </TabsTrigger>
            <TabsTrigger value="spaces" className="text-xs">
              Spaces
            </TabsTrigger>
            <TabsTrigger value="topics" className="text-xs">
              Topics
            </TabsTrigger>
          </TabsList>
          <TabsContent value="threads" className="mt-0">
            <ContinueTabPanel
              items={threadItems}
              emptyTitle="No chats yet"
              emptyDescription="Start a conversation to see it here"
              primaryCta="New chat"
              onPrimaryCta={() => navigate("/chat")}
            />
          </TabsContent>
          <TabsContent value="spaces" className="mt-0">
            <ContinueTabPanel
              items={spaceItems}
              emptyTitle="No spaces yet"
              emptyDescription="Create a space to organize your work"
              primaryCta="Browse spaces"
              onPrimaryCta={() => navigate("/spaces")}
            />
          </TabsContent>
          <TabsContent value="topics" className="mt-0">
            <ContinueTabPanel
              items={topicItems}
              emptyTitle="No topics yet"
              emptyDescription="Add topics inside a space to continue here"
              primaryCta="Go to spaces"
              onPrimaryCta={() => navigate("/spaces")}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
